import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { EopPlanSlug, buildPlanPageUrl, getBuildingIdFromUrl, getEopSidebar, getPlanSlugFromUrl, IEopSidebarEntry, IEopSidebarItem } from '../../eop-config/eop-page-config';

@Component({
  selector: 'small-sidebar',
  standalone: true,
  imports: [
    LucideAngularModule,
    CommonModule,
    RouterLink
  ],
  templateUrl: './small-sidebar.component.html',
  styleUrl: './small-sidebar.component.css'
})
export class SmallSidebarComponent {
  icons = Icons;
  pageRoute: string = '';
  currentPlan: EopPlanSlug = 'al';
  currentBuildingId: string | null = null;
  sidebarEntries: IEopSidebarEntry[] = [];

  @Input() sidebarStatus: string = '';  
  @Output() sidebarStatusChange = new EventEmitter<string>();

  constructor(
    private router: Router
  ) { }

  ngOnInit(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.updatePageRoute();
    });
    this.updatePageRoute();
  }

  private updatePageRoute() {
    const url = this.router.url.replace(/\/+$/, ''); // remove trailing slash
    this.currentPlan = getPlanSlugFromUrl(url);
    this.currentBuildingId = getBuildingIdFromUrl(url);
    this.sidebarEntries = getEopSidebar(this.currentPlan);
    const segments = url.split('/');
    this.pageRoute = segments[-1] || segments[segments.length - 1]; // get last segment
  }
  
  navigateToPage(route: string): void {
    if (!this.currentBuildingId) {
      return;
    }

    this.router.navigateByUrl(buildPlanPageUrl(this.currentPlan, this.currentBuildingId, route));
  }

  navigateEntry(entry: IEopSidebarEntry): void {
    if (!entry.route) {
      return;
    }

    this.navigateToPage(entry.route);
  }

  isEntryActive(entry: IEopSidebarEntry): boolean {
    if (entry.route) {
      return this.pageRoute === entry.route;
    }

    return !!entry.activeRoutes?.includes(this.pageRoute);
  }

  isItemActive(item: IEopSidebarItem): boolean {
    return this.pageRoute === item.route;
  }

  trackByEntry(_: number, entry: IEopSidebarEntry): string {
    return entry.route ?? entry.title;
  }

  trackByItem(_: number, item: IEopSidebarItem): string {
    return item.route;
  }

  getDropdownId(entry: IEopSidebarEntry): string {
    const mapping: Record<string, string> = {
      'Collaborative Planning Team': 'dropdown-planning',
      'NIMS & ICS': 'dropdown-nims-ics',
      'List of Materials': 'dropdown-materials',
      'Hazard-Specific Annexes': 'dropdown-hazard-annexes',
      'Functional Annexes': 'dropdown-functional-annexes',
      'Drill, Training, & Collaboration Reports': 'dropdown-reports',
      'Central Office': 'dropdown-central-office',

      'Emergency Contacts': 'dropdown-emergency-contacts',
      'Evacuation & Reunification': 'dropdown-evacuation-reunification',
      'Campus Specific Emergency Response Plans': 'dropdown-campus-specific-plans',
      'Drill, Training, and Collaboration Reports': 'dropdown-ms-reports',

      'School Information': 'dropdown-ga-school-info',
      'Facility Information': 'dropdown-ga-facility',
      'Evacuation & Transportation': 'dropdown-ga-evacuation',
      'Emergency Preparedness': 'dropdown-ga-preparedness',
      'Security & Access': 'dropdown-ga-security'
    };

    return mapping[entry.title] || '';
  }

  toggleSidebar(): void {
    this.sidebarStatus = this.sidebarStatus === 'small' ? 'large' : 'small';
    this.sidebarStatusChange.emit(this.sidebarStatus);
  }
}
